/**
 * Family panels setup: sections 04a / 04b / 04c + 05 NOISE of the controls sidebar.
 *
 * Extracted from createControls (CSS-04.4): public API unchanged. The parent
 * still owns syncVisibility, syncSliderValues, and syncI18n.
 */

import { defaultJitterForStamp } from "../../state";
import type { PatternBlendMode, Store, StampType } from "../../state";
import { registerAsset } from "../../util/asset-registry";
import { applyMethodPartial, type MethodPartial } from "../../util/method-patch";
import type { CustomSelectHandle } from "../custom-select";
import type { SliderVariant } from "../custom-slider";

interface SectionGroup {
  section: HTMLElement;
  body: HTMLDivElement;
  head: HTMLHeadingElement;
}

interface SliderRowOptions {
  labelKey: string;
  tipKey: string;
  ctrlId: string;
  min: number;
  max: number;
  step: number;
  value: number;
  variant?: SliderVariant;
  formatValue?: (n: number) => string;
}

interface SliderRow {
  row: HTMLDivElement;
  input: HTMLInputElement;
  valueEl: HTMLSpanElement;
}

interface CheckboxRow {
  row: HTMLDivElement;
  input: HTMLInputElement;
}

interface SelectRowOptions<T extends string> {
  labelKey: string;
  tipKey?: string;
  ctrlId: string;
  options: ReadonlyArray<readonly [T, string]>;
  value: T;
}

interface SelectRow {
  row: HTMLDivElement;
  select: HTMLSelectElement;
  root: HTMLDivElement;
  refresh: () => void;
}

interface FileRowOptions {
  triggerKey: string;
  tipKey?: string;
  ctrlId: string;
  accept: string;
}

interface FileRow {
  row: HTMLDivElement;
  input: HTMLInputElement;
  filenameEl: HTMLSpanElement;
}

export interface FamilyPanelsDeps {
  store: Store;
  root: HTMLElement;
  patchMethod: (partial: MethodPartial) => void;
  getIsSyncing: () => boolean;
  hasMethodSelectionTarget: (state: Store["state"]) => boolean;
  family: SelectRow;
  makeSection: (
    ix: string,
    titleKey: string,
    variant?: "" | "violet" | "yellow",
  ) => SectionGroup;
  makeRow: () => HTMLDivElement;
  makeLabelText: (
    labelKey: string,
    tooltipKey: string | undefined,
    htmlFor: string,
  ) => HTMLSpanElement;
  buildSliderRow: (opts: SliderRowOptions) => SliderRow;
  buildSelectRow: <T extends string>(opts: SelectRowOptions<T>) => SelectRow;
  buildFileRow: (opts: FileRowOptions) => FileRow;
  makeCustomSelect: (select: HTMLSelectElement) => CustomSelectHandle;
  readImageDataFromFile: (
    file: File,
    transparentPixels: "preserve" | "white",
  ) => Promise<ImageData>;
}

export interface FamilyPanelsRefs {
  halftoneSection: SectionGroup;
  customPatternSection: SectionGroup;
  dithertoneSection: SectionGroup;
  noiseSection: SectionGroup;
  aPanel: HTMLDivElement;
  cellSize: SliderRow;
  density: SliderRow;
  polarity: SelectRow;
  matrixRow: HTMLDivElement;
  matrixSelect: HTMLSelectElement;
  serpentineRow: HTMLDivElement;
  serpentineInput: HTMLInputElement;
  fsStrength: SliderRow;
  brightness: SliderRow;
  contrast: SliderRow;
  gridAngle: SliderRow;
  algorithmJitter: SliderRow;
  aPatternNoiseScale: SliderRow;
  grainStrength: SliderRow;
  grainScale: SliderRow;
  stampGroup: HTMLDivElement;
  stampType: SelectRow;
  coverage: SliderRow;
  shapeAngle: SliderRow;
  shapeFlipX: CheckboxRow;
  shapeFlipY: CheckboxRow;
  stampUploadRow: HTMLDivElement;
  patternBlendMode: SelectRow;
  patternInvertRow: HTMLDivElement;
  patternInvertInput: HTMLInputElement;
  patternBrightness: SliderRow;
  patternContrast: SliderRow;
  patternOpacity: SliderRow;
  patternScale: SliderRow;
  patternRotation: SliderRow;
  patternFlipX: CheckboxRow;
  patternFlipY: CheckboxRow;
  patternNoise: SliderRow;
  patternNoiseScale: SliderRow;
  blur: SliderRow;
  patternHalftoneEnabledInput: HTMLInputElement;
  patternHalftoneGroup: HTMLDivElement;
  patternHalftoneCellSize: SliderRow;
  patternHalftoneStampType: SelectRow;
  patternHalftoneStampUpload: FileRow;
  patternHalftoneCoverage: SliderRow;
  patternHalftonePolarity: SelectRow;
  patternHalftoneDensity: SliderRow;
  patternHalftoneBlackThreshold: SliderRow;
  patternHalftoneWhiteThreshold: SliderRow;
  patternHalftoneGridAngle: SliderRow;
  patternHalftoneShapeAngle: SliderRow;
  patternHalftoneShapeFlipX: CheckboxRow;
  patternHalftoneShapeFlipY: CheckboxRow;
  patternHalftoneAlgorithmJitter: SliderRow;
}

export function setupFamilyPanels(deps: FamilyPanelsDeps): FamilyPanelsRefs {
  const {
    store,
    root,
    patchMethod,
    getIsSyncing,
    hasMethodSelectionTarget,
    family,
    makeSection,
    makeRow,
    makeLabelText,
    buildSliderRow,
    buildSelectRow,
    buildFileRow,
    makeCustomSelect,
    readImageDataFromFile,
  } = deps;

  function buildCheckboxRow(
    labelKey: string,
    tipKey: string,
    ctrlId: string,
    checked: boolean,
  ): CheckboxRow {
    const row = makeRow();
    const input = document.createElement("input");
    input.type = "checkbox";
    input.checked = checked;
    input.id = ctrlId;
    const label = makeLabelText(labelKey, tipKey, input.id);
    row.appendChild(label);
    row.appendChild(input);
    return { row, input };
  }

  // ========== 04a/04b/04c HALFTONE / CUSTOM PATTERN / DITHERTONE + 05 NOISE ==========
  // M2 (3.2/3.3): 4 separate top-level sections per family + NOISE section.
  //
  // Three family sections (only the active family's section is visible at a time):
  //   04a HALFTONE       - yellow badge, Family A only
  //   04b CUSTOM PATTERN - violet badge, Family B only
  //   04c DITHERTONE     - violet badge, Family C only
  //
  // Plus one NOISE section whose colour alternates:
  //   05 NOISE           - violet when Family A active, yellow for B/C
  //
  // aPanel (Family A + C shared content) and bPanel are DOM-moved into the
  // correct section body in syncVisibility, so there is no row duplication.
  const halftoneSection = makeSection("04", "section.halftone", "yellow");
  // B3: data-family-section used by CSS to scope slider colour overrides.
  halftoneSection.section.dataset["familySection"] = "A";
  root.appendChild(halftoneSection.section);

  const customPatternSection = makeSection(
    "04",
    "section.customPattern",
    "violet",
  );
  customPatternSection.section.dataset["familySection"] = "B";
  root.appendChild(customPatternSection.section);

  const dithertoneSection = makeSection("04", "section.dithertone", "violet");
  // B3: Family C section -- CSS overrides .slider-wrap.yellow fills to violet
  // so aPanel rows (constructed with yellow variant) appear uniform when
  // DOM-moved into this section.
  dithertoneSection.section.dataset["familySection"] = "C";
  root.appendChild(dithertoneSection.section);

  const noiseSection = makeSection("05", "section.noise", "violet");
  root.appendChild(noiseSection.section);

  // ----- Family A + C: algorithm + stamp panel -----
  // aPanel lives in halftoneSection initially; syncVisibility moves it to
  // dithertoneSection when Family C is active (DOM move, no duplication).
  const aPanel = document.createElement("div");
  aPanel.className = "family-a";
  halftoneSection.body.appendChild(aPanel);

  const cellSize = buildSliderRow({
    labelKey: "param.cellSize",
    tipKey: "tip.cellSize",
    ctrlId: "ctrl-cellSize",
    min: 1,
    max: 32,
    step: 1,
    value: store.get().cellSize,
    variant: "yellow",
  });
  aPanel.appendChild(cellSize.row);
  cellSize.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(cellSize.input.value);
    cellSize.valueEl.textContent = String(v);
    patchMethod({ cellSize: v });
  });

  const density = buildSliderRow({
    labelKey: "param.density",
    tipKey: "tip.density",
    ctrlId: "ctrl-density",
    min: 0,
    max: 100,
    step: 1,
    value: store.get().density,
    variant: "yellow",
  });
  aPanel.appendChild(density.row);
  density.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(density.input.value);
    density.valueEl.textContent = String(v);
    patchMethod({ density: v });
  });

  const polarity = buildSelectRow({
    labelKey: "param.polarity",
    tipKey: "tip.polarity",
    ctrlId: "ctrl-polarity",
    options: [
      ["black", "param.polarity.black"],
      ["white", "param.polarity.white"],
    ] as const,
    value: store.get().polarity,
  });
  aPanel.appendChild(polarity.row);
  polarity.select.addEventListener("change", () => {
    patchMethod({ polarity: polarity.select.value as "black" | "white" });
  });

  // matrix select uses non-i18n option labels (raw NxN) so we keep it
  // styled as a fallback `.row-select` rather than upgrading to cselect.
  const matrixRow = makeRow();
  const matrixSelect = document.createElement("select");
  matrixSelect.className = "row-select";
  for (const v of [2, 4, 8, 16] as const) {
    const opt = document.createElement("option");
    opt.value = String(v);
    opt.textContent = `${v}×${v}`;
    matrixSelect.appendChild(opt);
  }
  matrixSelect.value = String(store.get().matrixSize);
  matrixSelect.id = "ctrl-matrix";
  const matrixLabel = makeLabelText(
    "param.matrixSize",
    "tip.matrixSize",
    matrixSelect.id,
  );
  matrixRow.appendChild(matrixLabel);
  matrixRow.appendChild(matrixSelect);
  // Wrap with cselect for visual parity even though options are not i18n keys.
  const matrixCselect = makeCustomSelect(matrixSelect);
  matrixRow.appendChild(matrixCselect.root);
  aPanel.appendChild(matrixRow);
  matrixSelect.addEventListener("change", () => {
    patchMethod({ matrixSize: Number(matrixSelect.value) as 2 | 4 | 8 | 16 });
  });

  const serpentineRow = makeRow();
  const serpentineInput = document.createElement("input");
  serpentineInput.type = "checkbox";
  serpentineInput.checked = store.get().serpentine;
  serpentineInput.id = "ctrl-serpentine";
  const serpentineLabel = makeLabelText(
    "param.serpentine",
    "tip.serpentine",
    serpentineInput.id,
  );
  serpentineRow.appendChild(serpentineLabel);
  serpentineRow.appendChild(serpentineInput);
  aPanel.appendChild(serpentineRow);
  serpentineInput.addEventListener("change", () => {
    patchMethod({ serpentine: serpentineInput.checked });
  });

  const fsStrength = buildSliderRow({
    labelKey: "param.strength",
    tipKey: "tip.strength",
    ctrlId: "ctrl-fsStrength",
    min: 0,
    max: 1,
    step: 0.05,
    value: store.get().fsStrength,
    formatValue: (n) => n.toFixed(2),
  });
  aPanel.appendChild(fsStrength.row);
  fsStrength.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(fsStrength.input.value);
    fsStrength.valueEl.textContent = v.toFixed(2);
    patchMethod({ fsStrength: v, atkinsonStrength: v });
  });

  const brightness = buildSliderRow({
    labelKey: "param.brightness",
    tipKey: "tip.brightness",
    ctrlId: "ctrl-brightness",
    min: -50,
    max: 50,
    step: 1,
    value: store.get().brightness,
  });
  aPanel.appendChild(brightness.row);
  brightness.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(brightness.input.value);
    brightness.valueEl.textContent = String(v);
    patchMethod({ brightness: v });
  });

  const contrast = buildSliderRow({
    labelKey: "param.contrast",
    tipKey: "tip.contrast",
    ctrlId: "ctrl-contrast",
    min: -50,
    max: 50,
    step: 1,
    value: store.get().contrast,
  });
  aPanel.appendChild(contrast.row);
  contrast.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(contrast.input.value);
    contrast.valueEl.textContent = String(v);
    patchMethod({ contrast: v });
  });

  const gridAngle = buildSliderRow({
    labelKey: "param.gridAngle",
    tipKey: "tip.gridAngle",
    ctrlId: "ctrl-gridAngle",
    min: 0,
    max: 180,
    step: 1,
    value: store.get().gridAngleDeg,
    formatValue: (n) => `${n}°`,
  });
  aPanel.appendChild(gridAngle.row);
  gridAngle.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(gridAngle.input.value);
    gridAngle.valueEl.textContent = `${v}°`;
    patchMethod({ gridAngleDeg: v });
  });

  // Family A noise rows are constructed here so closure references are
  // available where syncVisibility runs, but their DOM placement is
  // deferred until after the stamp sub-panel so the "NOISE" subsection
  // sits at the bottom of aPanel (visually grouped, not interleaved
  // with halftone params). See appendChild calls further down.
  const algorithmJitter = buildSliderRow({
    labelKey: "param.algorithmJitter",
    tipKey: "tip.algorithmJitter",
    ctrlId: "ctrl-algorithmJitter",
    min: 0,
    max: 1,
    step: 0.01,
    value: store.get().algorithmJitter,
    variant: "violet",
    formatValue: (n) => n.toFixed(2),
  });
  algorithmJitter.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(algorithmJitter.input.value);
    algorithmJitter.valueEl.textContent = v.toFixed(2);
    patchMethod({ algorithmJitter: v });
  });

  // 3.4 - Noise size (patternNoiseScale) also exposed for Family A Halftone.
  // A separate DOM node is required because the same node cannot appear in
  // both aPanel and bPanel simultaneously.
  const aPatternNoiseScale = buildSliderRow({
    labelKey: "param.patternNoiseScale",
    tipKey: "tip.patternNoiseScale",
    ctrlId: "ctrl-patternNoiseScale-a",
    min: 1,
    max: 16,
    step: 0.5,
    value: store.get().patternNoiseScale,
    variant: "violet",
    formatValue: (n) => n.toFixed(1),
  });
  aPatternNoiseScale.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(aPatternNoiseScale.input.value);
    aPatternNoiseScale.valueEl.textContent = v.toFixed(1);
    patchMethod({ patternNoiseScale: v });
  });

  const grainStrength = buildSliderRow({
    labelKey: "param.grainStrength",
    tipKey: "tip.grainStrength",
    ctrlId: "ctrl-grainStrength",
    min: 0,
    max: 1,
    step: 0.01,
    value: store.get().grainStrength,
    variant: "yellow",
    formatValue: (n) => n.toFixed(2),
  });
  grainStrength.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(grainStrength.input.value);
    grainStrength.valueEl.textContent = v.toFixed(2);
    patchMethod({ grainStrength: v });
  });

  const grainScale = buildSliderRow({
    labelKey: "param.grainScale",
    tipKey: "tip.grainScale",
    ctrlId: "ctrl-grainScale",
    min: 1,
    max: 16,
    step: 0.5,
    value: store.get().grainScale,
    variant: "yellow",
    formatValue: (n) => n.toFixed(1),
  });
  grainScale.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(grainScale.input.value);
    grainScale.valueEl.textContent = v.toFixed(1);
    patchMethod({ grainScale: v });
  });

  // ----- Stamp sub-panel (still inside section 03 for Family A) -----
  const stampGroup = document.createElement("div");
  stampGroup.className = "stamp-group";
  aPanel.appendChild(stampGroup);

  const stampType = buildSelectRow({
    labelKey: "label.stampShape",
    tipKey: "tip.stampShape",
    ctrlId: "ctrl-stampShape",
    options: [
      ["pixel", "stamp.pixel"],
      ["circle", "stamp.circle"],
      ["diamond", "stamp.diamond"],
      ["line", "stamp.line"],
      ["cross", "stamp.cross"],
      ["ellipse", "stamp.ellipse"],
      ["noise", "stamp.noise"],
      ["asterisk", "stamp.asterisk"],
      ["flower", "stamp.flower"],
      ["star", "stamp.star"],
      ["heart", "stamp.heart"],
      ["spade", "stamp.spade"],
      ["club", "stamp.club"],
      ["custom", "stamp.custom"],
    ] as const,
    value: store.get().stampType,
  });
  stampGroup.appendChild(stampType.row);
  stampType.select.addEventListener("change", () => {
    const newStamp = stampType.select.value as StampType;
    const oldDefault = defaultJitterForStamp(store.get().stampType);
    const methodPatch: MethodPartial = { stampType: newStamp };
    if (Math.abs(store.get().algorithmJitter - oldDefault) < 0.005) {
      methodPatch.algorithmJitter = defaultJitterForStamp(newStamp);
    }
    patchMethod(methodPatch);
  });

  /* Upload custom stamp positioning - Yuko UX adjustment 2026-05-15 */
  const stampUpload = buildFileRow({
    triggerKey: "action.upload.stamp",
    tipKey: "tip.customStamp",
    ctrlId: "ctrl-stampUpload",
    accept: "image/png",
  });
  const stampUploadRow = stampUpload.row;
  stampGroup.appendChild(stampUploadRow);
  stampUpload.input.addEventListener("change", () => {
    const file = stampUpload.input.files?.[0];
    if (file) {
      void readImageDataFromFile(file, "preserve").then((img) => {
        const current = store.get();
        const result = registerAsset(
          current.assetLibrary,
          current.assetData,
          current.assetDataAccessOrder,
          img,
          "stampMask",
        );
        store.patch({
          customStampMask: img,
          stampType: "custom",
          assetLibrary: result.assetLibrary,
          assetData: result.assetData,
          assetDataAccessOrder: result.assetDataAccessOrder,
        });
        stampType.select.value = "custom";
        stampType.select.dispatchEvent(new CustomEvent("cselect:sync"));
      });
    }
  });

  const coverage = buildSliderRow({
    labelKey: "param.coverage",
    tipKey: "tip.coverage",
    ctrlId: "ctrl-coverage",
    min: 0,
    max: 1.5,
    step: 0.05,
    value: store.get().stampCoverage,
    formatValue: (n) => n.toFixed(2),
  });
  stampGroup.appendChild(coverage.row);
  coverage.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(coverage.input.value);
    coverage.valueEl.textContent = v.toFixed(2);
    patchMethod({ stampCoverage: v });
  });

  // v0.2.0 had a line-only angle slider and a separate thickness
  // slider. Both now use the shared dot size and shape angle controls.

  const shapeAngle = buildSliderRow({
    labelKey: "param.shapeAngle",
    tipKey: "tip.shapeAngle",
    ctrlId: "ctrl-shapeAngle",
    min: 0,
    max: 180,
    step: 1,
    value: store.get().shapeAngleDeg,
    formatValue: (n) => `${n}°`,
  });
  stampGroup.appendChild(shapeAngle.row);
  shapeAngle.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(shapeAngle.input.value);
    shapeAngle.valueEl.textContent = `${v}°`;
    patchMethod({ shapeAngleDeg: v });
  });

  const shapeFlipX = buildCheckboxRow(
    "param.shapeFlipX",
    "tip.shapeFlipX",
    "ctrl-shapeFlipX",
    store.get().shapeFlipX,
  );
  const shapeFlipPair = document.createElement("div");
  shapeFlipPair.className = "control-pair control-pair-flips";
  stampGroup.appendChild(shapeFlipPair);
  shapeFlipPair.appendChild(shapeFlipX.row);
  shapeFlipX.input.addEventListener("change", () => {
    patchMethod({ shapeFlipX: shapeFlipX.input.checked });
  });

  const shapeFlipY = buildCheckboxRow(
    "param.shapeFlipY",
    "tip.shapeFlipY",
    "ctrl-shapeFlipY",
    store.get().shapeFlipY,
  );
  shapeFlipPair.appendChild(shapeFlipY.row);
  shapeFlipY.input.addEventListener("change", () => {
    patchMethod({ shapeFlipY: shapeFlipY.input.checked });
  });

  // All noise rows are appended to noiseSection.body at construction time.
  // syncVisibility uses display:none to show only the rows for the active family.
  noiseSection.body.appendChild(algorithmJitter.row);
  noiseSection.body.appendChild(aPatternNoiseScale.row);
  noiseSection.body.appendChild(grainStrength.row);
  noiseSection.body.appendChild(grainScale.row);

  // ----- Family B panel: Custom Pattern -----
  // bPanel lives in customPatternSection. Noise rows are moved to noiseSection
  // in syncVisibility (below).
  const bPanel = document.createElement("div");
  bPanel.className = "family-b";
  customPatternSection.body.appendChild(bPanel);

  const patternUpload = buildFileRow({
    triggerKey: "action.upload.pattern",
    tipKey: "tip.pattern",
    ctrlId: "ctrl-patternUpload",
    accept: "image/*",
  });
  bPanel.appendChild(patternUpload.row);
  patternUpload.input.addEventListener("change", () => {
    const file = patternUpload.input.files?.[0];
    if (file) {
      patternUpload.input.value = "";
      void readImageDataFromFile(file, "white").then((img) => {
        const current = store.get();
        if (hasMethodSelectionTarget(current)) {
          store.patch({
            patternImage: img,
            ...applyMethodPartial({ ...current, patternImage: img }, { family: "B" }),
          });
        } else {
          store.patch({ patternImage: img });
          patchMethod({ family: "B" });
        }
        family.select.value = "B";
        family.select.dispatchEvent(new CustomEvent("cselect:sync"));
      });
    }
  });

  const patternBlendMode = buildSelectRow({
    labelKey: "param.patternBlendMode",
    tipKey: "tip.patternBlendMode",
    ctrlId: "ctrl-patternBlendMode",
    options: [
      ["threshold", "option.blend.threshold"],
      ["multiply", "option.blend.multiply"],
      ["screen", "option.blend.screen"],
      ["overlay", "option.blend.overlay"],
      ["softlight", "option.blend.softlight"],
      ["pinlight", "option.blend.pinlight"],
    ] as const,
    value: store.get().patternBlendMode,
  });
  bPanel.appendChild(patternBlendMode.row);
  patternBlendMode.select.addEventListener("change", () => {
    const newMode = patternBlendMode.select.value as PatternBlendMode;
    // Mode-appropriate patternInvert default:
    // - threshold / multiply: ON (v0.2.2 WYSIWYG: black pattern = output black)
    // - screen / overlay / softlight / pinlight: OFF (pattern as-uploaded;
    //   invert would flip a typical white-bg/black-shape pattern to
    //   black-bg/white-shape and quietly negate Screen/Overlay's effect).
    // We only nudge the toggle when the user wasn't deliberately holding
    // it against the previous mode's default - if they'd already toggled
    // it OFF on Threshold or ON on Pin Light, keep their choice intact.
    const prevMode = store.get().patternBlendMode;
    const prevDefault = prevMode === "threshold" || prevMode === "multiply";
    const newDefault = newMode === "threshold" || newMode === "multiply";
    const methodPatch: MethodPartial = { patternBlendMode: newMode };
    if (store.get().patternInvert === prevDefault) {
      methodPatch.patternInvert = newDefault;
      patternInvertInput.checked = newDefault;
    }
    patchMethod(methodPatch);
  });

  // Pattern invert toggle. Default ON in v0.2.2 → WYSIWYG semantic
  // (white pattern background stays white in the output, black
  // shapes paint black). Toggle OFF restores the v0.2.1 inverted
  // behaviour (pattern as a "min brightness to stay white"
  // threshold map) for users who want to keep tuning that way.
  const patternInvertRow = makeRow();
  const patternInvertInput = document.createElement("input");
  patternInvertInput.type = "checkbox";
  patternInvertInput.checked = store.get().patternInvert;
  patternInvertInput.id = "ctrl-patternInvert";
  const patternInvertLabel = makeLabelText(
    "param.patternInvert",
    "tip.patternInvert",
    patternInvertInput.id,
  );
  patternInvertRow.appendChild(patternInvertLabel);
  patternInvertRow.appendChild(patternInvertInput);
  bPanel.appendChild(patternInvertRow);
  patternInvertInput.addEventListener("change", () => {
    patchMethod({ patternInvert: patternInvertInput.checked });
  });

  const patternBrightness = buildSliderRow({
    labelKey: "param.patternBrightness",
    tipKey: "tip.patternBrightness",
    ctrlId: "ctrl-patternBrightness",
    min: -100,
    max: 100,
    step: 1,
    value: store.get().patternBrightness,
    variant: "violet",
  });
  bPanel.appendChild(patternBrightness.row);
  patternBrightness.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternBrightness.input.value);
    patternBrightness.valueEl.textContent = String(v);
    patchMethod({ patternBrightness: v });
  });

  const patternContrast = buildSliderRow({
    labelKey: "param.patternContrast",
    tipKey: "tip.patternContrast",
    ctrlId: "ctrl-patternContrast",
    min: -100,
    max: 100,
    step: 1,
    value: store.get().patternContrast,
    variant: "violet",
  });
  bPanel.appendChild(patternContrast.row);
  patternContrast.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternContrast.input.value);
    patternContrast.valueEl.textContent = String(v);
    patchMethod({ patternContrast: v });
  });

  const patternOpacity = buildSliderRow({
    labelKey: "param.patternOpacity",
    tipKey: "tip.patternOpacity",
    ctrlId: "ctrl-patternOpacity",
    min: 0,
    max: 1,
    step: 0.01,
    value: store.get().patternOpacity,
    variant: "violet",
    formatValue: (n) => n.toFixed(2),
  });
  bPanel.appendChild(patternOpacity.row);
  patternOpacity.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternOpacity.input.value);
    patternOpacity.valueEl.textContent = v.toFixed(2);
    patchMethod({ patternOpacity: v });
  });

  const patternScale = buildSliderRow({
    labelKey: "param.patternScale",
    tipKey: "tip.patternScale",
    ctrlId: "ctrl-patternScale",
    min: 0.01,
    max: 4,
    step: 0.01,
    value: store.get().patternScale,
    variant: "violet",
    formatValue: (n) => n.toFixed(2),
  });
  bPanel.appendChild(patternScale.row);
  patternScale.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternScale.input.value);
    patternScale.valueEl.textContent = v.toFixed(2);
    patchMethod({ patternScale: v });
  });

  const patternRotation = buildSliderRow({
    labelKey: "param.patternRotation",
    tipKey: "tip.patternRotation",
    ctrlId: "ctrl-patternRotation",
    min: 0,
    max: 360,
    step: 1,
    value: store.get().patternRotationDeg,
    variant: "violet",
    formatValue: (n) => `${n}°`,
  });
  bPanel.appendChild(patternRotation.row);
  patternRotation.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternRotation.input.value);
    patternRotation.valueEl.textContent = `${v}°`;
    patchMethod({ patternRotationDeg: v });
  });

  const patternFlipX = buildCheckboxRow(
    "param.patternFlipX",
    "tip.patternFlipX",
    "ctrl-patternFlipX",
    store.get().patternFlipX,
  );
  const patternFlipPair = document.createElement("div");
  patternFlipPair.className = "control-pair control-pair-flips";
  bPanel.appendChild(patternFlipPair);
  patternFlipPair.appendChild(patternFlipX.row);
  patternFlipX.input.addEventListener("change", () => {
    patchMethod({ patternFlipX: patternFlipX.input.checked });
  });

  const patternFlipY = buildCheckboxRow(
    "param.patternFlipY",
    "tip.patternFlipY",
    "ctrl-patternFlipY",
    store.get().patternFlipY,
  );
  patternFlipPair.appendChild(patternFlipY.row);
  patternFlipY.input.addEventListener("change", () => {
    patchMethod({ patternFlipY: patternFlipY.input.checked });
  });

  // Family B noise rows - built here so visibility wiring (further
  // below) has the closure references, but appended at the bottom of
  // bPanel so the "NOISE" subsection sits below the pattern adjust
  // block. See appendChild calls at end of bPanel.
  const patternNoise = buildSliderRow({
    labelKey: "param.patternNoise",
    tipKey: "tip.patternNoise",
    ctrlId: "ctrl-patternNoise",
    min: 0,
    max: 1,
    step: 0.01,
    value: store.get().patternNoise,
    variant: "yellow",
    formatValue: (n) => n.toFixed(2),
  });
  patternNoise.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternNoise.input.value);
    patternNoise.valueEl.textContent = v.toFixed(2);
    patchMethod({ patternNoise: v });
  });

  const patternNoiseScale = buildSliderRow({
    labelKey: "param.patternNoiseScale",
    tipKey: "tip.patternNoiseScale",
    ctrlId: "ctrl-patternNoiseScale",
    min: 1,
    max: 16,
    step: 0.5,
    value: store.get().patternNoiseScale,
    variant: "yellow",
    formatValue: (n) => n.toFixed(1),
  });
  patternNoiseScale.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternNoiseScale.input.value);
    patternNoiseScale.valueEl.textContent = v.toFixed(1);
    patchMethod({ patternNoiseScale: v });
  });

  const patternHalftoneEnabledRow = makeRow();
  const patternHalftoneEnabledInput = document.createElement("input");
  patternHalftoneEnabledInput.type = "checkbox";
  patternHalftoneEnabledInput.checked = store.get().patternHalftoneEnabled;
  patternHalftoneEnabledInput.id = "ctrl-patternHalftoneEnabled";
  const patternHalftoneEnabledLabel = makeLabelText(
    "param.patternHalftoneEnabled",
    "tip.patternHalftoneEnabled",
    patternHalftoneEnabledInput.id,
  );
  patternHalftoneEnabledRow.appendChild(patternHalftoneEnabledLabel);
  patternHalftoneEnabledRow.appendChild(patternHalftoneEnabledInput);
  bPanel.appendChild(patternHalftoneEnabledRow);

  const patternHalftoneGroup = document.createElement("div");
  patternHalftoneGroup.className = "stamp-group";
  patternHalftoneGroup.dataset["controlGroup"] = "pattern-halftone-postprocess";
  bPanel.appendChild(patternHalftoneGroup);

  const patternHalftoneCellSize = buildSliderRow({
    labelKey: "param.cellSize",
    tipKey: "tip.cellSize",
    ctrlId: "ctrl-patternHalftoneCellSize",
    min: 1,
    max: 32,
    step: 1,
    value: store.get().patternHalftoneCellSize,
    variant: "yellow",
  });
  patternHalftoneGroup.appendChild(patternHalftoneCellSize.row);
  patternHalftoneCellSize.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternHalftoneCellSize.input.value);
    patternHalftoneCellSize.valueEl.textContent = String(v);
    patchMethod({ patternHalftoneCellSize: v });
  });

  const patternHalftoneStampType = buildSelectRow({
    labelKey: "label.stampShape",
    tipKey: "tip.stampShape",
    ctrlId: "ctrl-patternHalftoneStampShape",
    options: [
      ["pixel", "stamp.pixel"],
      ["circle", "stamp.circle"],
      ["diamond", "stamp.diamond"],
      ["line", "stamp.line"],
      ["cross", "stamp.cross"],
      ["ellipse", "stamp.ellipse"],
      ["noise", "stamp.noise"],
      ["asterisk", "stamp.asterisk"],
      ["flower", "stamp.flower"],
      ["star", "stamp.star"],
      ["heart", "stamp.heart"],
      ["spade", "stamp.spade"],
      ["club", "stamp.club"],
      ["custom", "stamp.custom"],
    ] as const,
    value: store.get().patternHalftoneStampType,
  });
  patternHalftoneGroup.appendChild(patternHalftoneStampType.row);
  patternHalftoneStampType.select.addEventListener("change", () => {
    const newStamp = patternHalftoneStampType.select.value as StampType;
    const oldDefault = defaultJitterForStamp(store.get().patternHalftoneStampType);
    const methodPatch: MethodPartial = { patternHalftoneStampType: newStamp };
    if (Math.abs(store.get().patternHalftoneAlgorithmJitter - oldDefault) < 0.005) {
      methodPatch.patternHalftoneAlgorithmJitter = defaultJitterForStamp(newStamp);
    }
    patchMethod(methodPatch);
  });

  /* Upload custom stamp positioning - Yuko UX adjustment 2026-05-15 */
  const patternHalftoneStampUpload = buildFileRow({
    triggerKey: "action.upload.stamp",
    tipKey: "tip.customStamp",
    ctrlId: "ctrl-patternHalftoneStampUpload",
    accept: "image/png",
  });
  patternHalftoneGroup.appendChild(patternHalftoneStampUpload.row);
  patternHalftoneStampUpload.input.addEventListener("change", () => {
    const file = patternHalftoneStampUpload.input.files?.[0];
    if (file) {
      void readImageDataFromFile(file, "preserve").then((img) => {
        const current = store.get();
        const result = registerAsset(
          current.assetLibrary,
          current.assetData,
          current.assetDataAccessOrder,
          img,
          "stampMask",
        );
        store.patch({
          customStampMask: img,
          assetLibrary: result.assetLibrary,
          assetData: result.assetData,
          assetDataAccessOrder: result.assetDataAccessOrder,
        });
        patchMethod({ patternHalftoneStampType: "custom" });
        patternHalftoneStampType.select.value = "custom";
        patternHalftoneStampType.select.dispatchEvent(new CustomEvent("cselect:sync"));
      });
    }
  });

  const patternHalftoneCoverage = buildSliderRow({
    labelKey: "param.coverage",
    tipKey: "tip.coverage",
    ctrlId: "ctrl-patternHalftoneCoverage",
    min: 0,
    max: 1.5,
    step: 0.05,
    value: store.get().patternHalftoneStampCoverage,
    formatValue: (n) => n.toFixed(2),
  });
  patternHalftoneGroup.appendChild(patternHalftoneCoverage.row);
  patternHalftoneCoverage.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternHalftoneCoverage.input.value);
    patternHalftoneCoverage.valueEl.textContent = v.toFixed(2);
    patchMethod({ patternHalftoneStampCoverage: v });
  });

  const patternHalftonePolarity = buildSelectRow({
    labelKey: "param.polarity",
    tipKey: "tip.polarity",
    ctrlId: "ctrl-patternHalftonePolarity",
    options: [
      ["black", "param.polarity.black"],
      ["white", "param.polarity.white"],
    ] as const,
    value: store.get().patternHalftonePolarity,
  });
  patternHalftoneGroup.appendChild(patternHalftonePolarity.row);
  patternHalftonePolarity.select.addEventListener("change", () => {
    patchMethod({
      patternHalftonePolarity: patternHalftonePolarity.select.value as
        | "black"
        | "white",
    });
  });

  const patternHalftoneDensity = buildSliderRow({
    labelKey: "param.density",
    tipKey: "tip.density",
    ctrlId: "ctrl-patternHalftoneDensity",
    min: 0,
    max: 100,
    step: 1,
    value: store.get().patternHalftoneDensity,
    variant: "yellow",
  });
  patternHalftoneGroup.appendChild(patternHalftoneDensity.row);
  patternHalftoneDensity.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternHalftoneDensity.input.value);
    patternHalftoneDensity.valueEl.textContent = String(v);
    patchMethod({ patternHalftoneDensity: v });
  });

  const patternHalftoneBlackThreshold = buildSliderRow({
    labelKey: "param.blackThreshold",
    tipKey: "tip.blackThreshold",
    ctrlId: "ctrl-patternHalftoneBlackThreshold",
    min: 0,
    max: 0.5,
    step: 0.01,
    value: store.get().patternHalftoneBlackThreshold,
    formatValue: (n) => n.toFixed(2),
  });
  patternHalftoneGroup.appendChild(patternHalftoneBlackThreshold.row);
  patternHalftoneBlackThreshold.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternHalftoneBlackThreshold.input.value);
    patternHalftoneBlackThreshold.valueEl.textContent = v.toFixed(2);
    patchMethod({ patternHalftoneBlackThreshold: v });
  });

  const patternHalftoneWhiteThreshold = buildSliderRow({
    labelKey: "param.whiteThreshold",
    tipKey: "tip.whiteThreshold",
    ctrlId: "ctrl-patternHalftoneWhiteThreshold",
    min: 0.5,
    max: 1,
    step: 0.01,
    value: store.get().patternHalftoneWhiteThreshold,
    formatValue: (n) => n.toFixed(2),
  });
  patternHalftoneGroup.appendChild(patternHalftoneWhiteThreshold.row);
  patternHalftoneWhiteThreshold.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternHalftoneWhiteThreshold.input.value);
    patternHalftoneWhiteThreshold.valueEl.textContent = v.toFixed(2);
    patchMethod({ patternHalftoneWhiteThreshold: v });
  });

  const patternHalftoneGridAngle = buildSliderRow({
    labelKey: "param.gridAngle",
    tipKey: "tip.gridAngle",
    ctrlId: "ctrl-patternHalftoneGridAngle",
    min: 0,
    max: 180,
    step: 1,
    value: store.get().patternHalftoneGridAngleDeg,
    formatValue: (n) => `${n}°`,
  });
  patternHalftoneGroup.appendChild(patternHalftoneGridAngle.row);
  patternHalftoneGridAngle.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternHalftoneGridAngle.input.value);
    patternHalftoneGridAngle.valueEl.textContent = `${v}°`;
    patchMethod({ patternHalftoneGridAngleDeg: v });
  });

  const patternHalftoneShapeAngle = buildSliderRow({
    labelKey: "param.shapeAngle",
    tipKey: "tip.shapeAngle",
    ctrlId: "ctrl-patternHalftoneShapeAngle",
    min: 0,
    max: 180,
    step: 1,
    value: store.get().patternHalftoneShapeAngleDeg,
    formatValue: (n) => `${n}°`,
  });
  patternHalftoneGroup.appendChild(patternHalftoneShapeAngle.row);
  patternHalftoneShapeAngle.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternHalftoneShapeAngle.input.value);
    patternHalftoneShapeAngle.valueEl.textContent = `${v}°`;
    patchMethod({ patternHalftoneShapeAngleDeg: v });
  });

  const patternHalftoneShapeFlipX = buildCheckboxRow(
    "param.shapeFlipX",
    "tip.shapeFlipX",
    "ctrl-patternHalftoneShapeFlipX",
    store.get().patternHalftoneShapeFlipX,
  );
  const patternHalftoneShapeFlipPair = document.createElement("div");
  patternHalftoneShapeFlipPair.className = "control-pair control-pair-flips";
  patternHalftoneGroup.appendChild(patternHalftoneShapeFlipPair);
  patternHalftoneShapeFlipPair.appendChild(patternHalftoneShapeFlipX.row);
  patternHalftoneShapeFlipX.input.addEventListener("change", () => {
    patchMethod({
      patternHalftoneShapeFlipX: patternHalftoneShapeFlipX.input.checked,
    });
  });

  const patternHalftoneShapeFlipY = buildCheckboxRow(
    "param.shapeFlipY",
    "tip.shapeFlipY",
    "ctrl-patternHalftoneShapeFlipY",
    store.get().patternHalftoneShapeFlipY,
  );
  patternHalftoneShapeFlipPair.appendChild(patternHalftoneShapeFlipY.row);
  patternHalftoneShapeFlipY.input.addEventListener("change", () => {
    patchMethod({
      patternHalftoneShapeFlipY: patternHalftoneShapeFlipY.input.checked,
    });
  });

  const patternHalftoneAlgorithmJitter = buildSliderRow({
    labelKey: "param.algorithmJitter",
    tipKey: "tip.algorithmJitter",
    ctrlId: "ctrl-patternHalftoneAlgorithmJitter",
    min: 0,
    max: 1,
    step: 0.01,
    value: store.get().patternHalftoneAlgorithmJitter,
    variant: "violet",
    formatValue: (n) => n.toFixed(2),
  });
  patternHalftoneGroup.appendChild(patternHalftoneAlgorithmJitter.row);
  patternHalftoneAlgorithmJitter.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(patternHalftoneAlgorithmJitter.input.value);
    patternHalftoneAlgorithmJitter.valueEl.textContent = v.toFixed(2);
    patchMethod({ patternHalftoneAlgorithmJitter: v });
  });

  patternHalftoneEnabledInput.addEventListener("change", () => {
    patchMethod({ patternHalftoneEnabled: patternHalftoneEnabledInput.checked });
  });

  const blur = buildSliderRow({
    labelKey: "param.blurRadius",
    tipKey: "tip.blurRadius",
    ctrlId: "ctrl-blurRadius",
    min: 0,
    max: 16,
    step: 1,
    value: store.get().blurRadius,
    variant: "violet",
    formatValue: (n) => `${n}px`,
  });
  bPanel.appendChild(blur.row);
  blur.input.addEventListener("input", () => {
    if (getIsSyncing()) return;
    const v = Number(blur.input.value);
    blur.valueEl.textContent = `${v}px`;
    patchMethod({ blurRadius: v });
  });

  // Family B noise rows also appended to noiseSection.body at construction time.
  noiseSection.body.appendChild(patternNoise.row);
  noiseSection.body.appendChild(patternNoiseScale.row);

  return {
    halftoneSection,
    customPatternSection,
    dithertoneSection,
    noiseSection,
    aPanel,
    cellSize,
    density,
    polarity,
    matrixRow,
    matrixSelect,
    serpentineRow,
    serpentineInput,
    fsStrength,
    brightness,
    contrast,
    gridAngle,
    algorithmJitter,
    aPatternNoiseScale,
    grainStrength,
    grainScale,
    stampGroup,
    stampType,
    coverage,
    shapeAngle,
    shapeFlipX,
    shapeFlipY,
    stampUploadRow,
    patternBlendMode,
    patternInvertRow,
    patternInvertInput,
    patternBrightness,
    patternContrast,
    patternOpacity,
    patternScale,
    patternRotation,
    patternFlipX,
    patternFlipY,
    patternNoise,
    patternNoiseScale,
    blur,
    patternHalftoneEnabledInput,
    patternHalftoneGroup,
    patternHalftoneCellSize,
    patternHalftoneStampType,
    patternHalftoneStampUpload,
    patternHalftoneCoverage,
    patternHalftonePolarity,
    patternHalftoneDensity,
    patternHalftoneBlackThreshold,
    patternHalftoneWhiteThreshold,
    patternHalftoneGridAngle,
    patternHalftoneShapeAngle,
    patternHalftoneShapeFlipX,
    patternHalftoneShapeFlipY,
    patternHalftoneAlgorithmJitter,
  };
}
